import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import {
    TextButton,
    FormHelp,
    Label,
    LabeledControl,
    Radio,
    Spacer,
    Text,
    Flex,
    List,
} from '@zillow/constellation';

import { Col, Row, Tag } from '@zillow/zrs-comet';
import { LabsterActions } from '@zg-rentals/particles-js-labster';

import analyticsUtils from 'app/client/utils/analyticsUtils';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import appLogger from 'app/shared/loggers/appLogger';

import { StyledSubTitleContainer, StyledListItem } from './StyledComponents';

const propTypes = {
    errorObj: PropTypes.object.isRequired,
    handleSelectionChange: PropTypes.func.isRequired,
    isApplicationsAccepted: PropTypes.bool,
    isApplicationsAcceptedRef: PropTypes.object.isRequired,
};

const defaultProps = {
    isApplicationsAccepted: null,
};

const RADIO_OPTIONS = {
    YES: {
        DISPLAY: 'Yes',
        VALUE: true,
    },
    NO: {
        DISPLAY: 'No',
        VALUE: false,
    },
};

const copy = `Save time by allowing renters to apply directly to your listing.
    You'll receive a completed application, background check, credit report
    and eviction history – all at no cost to you.`;
const learnMoreUrl = 'https://www.zillow.com/z/rental-manager/tenant-screening/';

const EnableApplicationsRadio = ({
    errorObj: { isApplicationsAccepted: errorMessage },
    handleSelectionChange,
    isApplicationsAccepted,
    isApplicationsAcceptedRef,
}) => {
    const dispatch = useDispatch();
    const isExperiment = dispatch(LabsterActions.isExperiment('applications-enablement-detail-report'));
    const [selectedOption, setSelectedOption] = useState(isApplicationsAccepted);

    // Gets the event action in the form of ToSelected-from-FromSelected
    const getEventAction = (selected) => {
        let fromSelectedDisplay = selectedOption;
        const toSelectedDisplay = selected ? 'Enable' : 'Disable';

        if (fromSelectedDisplay !== null) {
            fromSelectedDisplay = fromSelectedDisplay ? 'Enabled' : 'Disabled';
        }

        return `${toSelectedDisplay}From${fromSelectedDisplay}`;
    };

    const triggerGAEvent = (selected) => {
        const eventAction = getEventAction(selected);
        const { APPLICATIONS_RADIO_CHANGE } = AnalyticsConstants.GA_EVENTS;

        APPLICATIONS_RADIO_CHANGE.action = eventAction;

        appLogger.logInfo({
            message: `INFO: ${JSON.stringify(APPLICATIONS_RADIO_CHANGE)}`,
            context: 'EnableApplicationsRadio.triggerGAEvent',
        });

        dispatch(analyticsUtils.track.GAEventV2(APPLICATIONS_RADIO_CHANGE));
    };

    const handleChange = (selected) => {
        setSelectedOption(selected);
        triggerGAEvent(selected);
        handleSelectionChange('isApplicationsAccepted', selected);
    };

    const renderYesLabel = () => (
        <Label>
            {RADIO_OPTIONS.YES.DISPLAY}
            &nbsp;
            <b>(More common)</b>
        </Label>
    );

    const renderNoLabel = () => (
        <Label>
            {RADIO_OPTIONS.NO.DISPLAY}
            &nbsp;
            <b>(Less common)&nbsp;</b>
    - I will manually collect all application data from renters and run credit and criminal checks separately.
        </Label>
    );

    // Known error: Failed prop type: Invalid prop `label` of type `object` supplied to `Radio`, expected `string`
    // renderYesLabel returns jsx in order to style part of the label
    const renderRadioOptions = () => (
        <Spacer marginBottom="sm">
            <Row>
                <Col>
                    <div>
                        <LabeledControl
                            controlId="LabeledControl-EnableApplicationsRadio1"
                            label={(renderYesLabel())}
                            control={(
                                <Radio
                                    name="EnableApplicationsRadio"
                                    checked={selectedOption === RADIO_OPTIONS.YES.VALUE}
                                    onChange={() => handleChange(RADIO_OPTIONS.YES.VALUE)}
                                />
                            )}
                        />
                        <LabeledControl
                            controlId="LabeledControl-EnableApplicationsRadio2"
                            label={renderNoLabel()}
                            control={(
                                <Radio
                                    name="EnableApplicationsRadio"
                                    checked={selectedOption === RADIO_OPTIONS.NO.VALUE}
                                    onChange={() => handleChange(RADIO_OPTIONS.NO.VALUE)}
                                />
                            )}
                        />
                    </div>
                </Col>
            </Row>
        </Spacer>
    );

    if (isExperiment) {
        return (
            <div className="EnableApplicationRadio-ABTest">
                <Spacer marginBottom="xl">
                    <div ref={isApplicationsAcceptedRef} />
                    <Text marginBottom="xs" fontType="h4" as="div">Receive Zillow applications?</Text>
                    <Text marginBottom="xs" as="div">
                        Access all applications &#38; documents in one place -- for free!
                    </Text>
                    <Text as="div">
                        You&#39;ll automatically receive:
                    </Text>
                    <List>
                        <StyledListItem><Text>Credit report</Text></StyledListItem>
                        <StyledListItem><Text>Background check</Text></StyledListItem>
                        <StyledListItem>
                            <Text>Income &#38; employment history</Text>
                        </StyledListItem>
                        <StyledListItem>
                            <Flex display="flex">
                                <Text>Proof-of-income (if provided)</Text>
                                <Spacer marginLeft="xs">
                                    <Tag appearance="success" size="sm">NEW</Tag>
                                </Spacer>
                            </Flex>
                        </StyledListItem>
                    </List>
                    <Spacer marginY="xs">
                        <LabeledControl
                            controlId="LabeledControl-EnableApplicationsRadio1"
                            label={(
                                <Label>
                                    <Text>
                                        Yes, receive Zillow applications
                                    </Text>
                                </Label>
                            )}
                            control={(
                                <Radio
                                    name="EnableApplicationsRadio"
                                    checked={selectedOption === RADIO_OPTIONS.YES.VALUE}
                                    onChange={() => handleChange(RADIO_OPTIONS.YES.VALUE)}
                                />
                            )}
                        />
                        <LabeledControl
                            controlId="LabeledControl-EnableApplicationsRadio2"
                            label={(
                                <Label>
                                    <Text>
                                        No, don&#39;t receive Zillow applications
                                    </Text>
                                </Label>
                            )}
                            control={(
                                <Radio
                                    name="EnableApplicationsRadio"
                                    checked={selectedOption === RADIO_OPTIONS.NO.VALUE}
                                    onChange={() => handleChange(RADIO_OPTIONS.NO.VALUE)}
                                />
                            )}
                        />
                    </Spacer>
                    {errorMessage && <FormHelp error={true}>{errorMessage}</FormHelp>}
                </Spacer>
            </div>
        );
    }

    return (
        <div className="EnableApplicationRadio">
            <Spacer marginBottom="xl">
                <div ref={isApplicationsAcceptedRef} />
                <Text marginBottom="xs" fontType="h4" as="div">Rental applications</Text>
                <StyledSubTitleContainer>
                    <Label marginBottom={0.5} marginRight={0.5} size="lg" required={true}>
                        Do you want to accept Zillow rental applications for this listing?&nbsp;
                        <TextButton as="a" href={learnMoreUrl} target="_blank">Learn More</TextButton>
                    </Label>
                </StyledSubTitleContainer>
                <Text marginBottom="sm" fontType="bodySmall" as="div">{copy}</Text>
                {renderRadioOptions()}
                {errorMessage && <FormHelp error={true}>{errorMessage}</FormHelp>}
            </Spacer>
        </div>
    );
};

EnableApplicationsRadio.propTypes = propTypes;
EnableApplicationsRadio.defaultProps = defaultProps;

export { EnableApplicationsRadio };
export default EnableApplicationsRadio;
